import React, { useMemo, useRef, useState } from 'react';
import { Heading5, Label, Body } from '~/components/atom/Typography';
import JustificationContainer from '~/components/atom/JustificationContainer';
import GridContainer from '~/components/atom/GridContainer';
import SelectBlock from '~/components/molecule/SelectBlock';
import AnimatedCheckbox from '~/components/molecule/AnimatedCheckbox';
import Button from '~/components/atom/Button';
import Input from '~/components/molecule/Input';
import Icon from '~/components/atom/Icon';
import Divider from '~/components/atom/Divider';
import LfaSettingsStatus from '~/components/page/Settings/LFASettings/components/UpdateSettingsForm/components/LfaSettingsStatus';
import {
  Lfa_Conversion_Location_Type,
  type LfaCalendarV2FieldsFragment,
  type LfaServicesV2FieldsFragment,
  type GetLfaConversionDataQuery,
  type LfaServiceCalendarRefFieldsFragment,
} from '~/graphql/types';
import type { ServicesV2Action } from '../../../reducers/servicesV2Reducer';
import type { OfficeChangeFragment } from '~/components/page/Settings/LFASettings/servicesV2/types';
import formatOrdinal from '~/LFA/utils/formatOrdinal';
import useOffices from '~/hooks/useOffices';
import LocationSettingsPopupV2 from '../LocationSettingsPopupV2';
import CalendarSettingsPopupV2 from '../CalendarSettingsPopupV2';
import hasValue from '~/util/hasValue';

const text = {
  selectLocations: 'Selecteer beschikbare locatieopties',
  locationError: 'Selecteer minimaal één locatie',
  criteria: 'Criteria voor agenten en AI',
  addCriterion: 'Criterium toevoegen',
  criterionPlaceholder: 'Voer een criterium in...',
  calendars: "Gekoppelde agenda's",
  noCalendars: "Geen agenda's beschikbaar. Voeg eerst agenda's toe.",
  preferred: 'voorkeur',
  officesOverride: (count: number) =>
    count === 1
      ? '1 vestiging heeft afwijkende instellingen'
      : `${count} vestigingen hebben afwijkende instellingen`,
  officeAddConflict: (officeNames: Array<string>) =>
    `Deze dienst is ingeschakeld bij ${officeNames.join(', ')}. Schakel deze eerst uit voordat je de dienst accountbreed inschakelt.`,
};

const locationLabelMap: Record<Lfa_Conversion_Location_Type, string> = {
  Office: 'Kantoor',
  OnLocation: 'Op locatie',
  Phone: 'Telefoon',
};

const ALL_LOCATION_TYPES: Array<Lfa_Conversion_Location_Type> = [
  Lfa_Conversion_Location_Type.Office,
  Lfa_Conversion_Location_Type.OnLocation,
  Lfa_Conversion_Location_Type.Phone,
];

type AccountService = LfaServicesV2FieldsFragment['accountServices'][number];
type AccountServiceLocation = AccountService['locations'][number];

type Props = {
  servicesV2: LfaServicesV2FieldsFragment;
  allServices: GetLfaConversionDataQuery['getLfaConversionData']['allServices'];
  dispatch: React.Dispatch<ServicesV2Action>;
};

const getChangeType = (change: OfficeChangeFragment): string => {
  switch (change.__typename) {
    case 'LFA_Office_Change_Add_V2':
      return 'toegevoegd';
    case 'LFA_Office_Change_Remove_V2':
      return 'verwijderd';
    case 'LFA_Office_Change_Mutate_V2':
      return 'aangepast';
  }
};

const LocationItemV2: React.FC<{
  serviceId: string;
  locationType: Lfa_Conversion_Location_Type;
  location: AccountServiceLocation | undefined;
  isLast: boolean;
  dispatch: React.Dispatch<ServicesV2Action>;
}> = ({ serviceId, locationType, location, isLast, dispatch }) => {
  const [showSettings, setShowSettings] = useState(false);
  const openerRef = useRef<HTMLDivElement>(null);
  const isEnabled = !!location;
  const note = location?.note?.trim();
  const isPreferred = isEnabled && location.weight > 0;

  return (
    <JustificationContainer
      width="100%"
      justification="space-between"
      direction="column"
    >
      <JustificationContainer
        width="100%"
        align="center"
        gap="s"
        justification="space-between"
      >
        <AnimatedCheckbox
          selectable
          label={
            <JustificationContainer align="center" gap="s">
              <Label size="base" margin={['xxxs', null, null, null]}>
                {locationLabelMap[locationType]}
                {hasValue(note) && `\u00a0(${note})`}
              </Label>
              {isPreferred && (
                <LfaSettingsStatus
                  label={`1ste ${text.preferred}`}
                  backgroundColor={{
                    group: 'success',
                    variant: 'translucent',
                  }}
                  textColor={{ group: 'success' }}
                />
              )}
            </JustificationContainer>
          }
          value={isEnabled}
          onChange={() =>
            dispatch({
              type: 'V2_TOGGLE_SERVICE_LOCATION',
              payload: { serviceId, locationType },
            })
          }
        />
        <Icon
          ref={openerRef}
          name="gear"
          onClick={() => isEnabled && setShowSettings(!showSettings)}
          style={{ visibility: isEnabled ? 'visible' : 'hidden' }}
          aria-hidden={!isEnabled}
        />
      </JustificationContainer>

      {showSettings && isEnabled && (
        <LocationSettingsPopupV2
          openerRef={openerRef}
          note={location.note ?? ''}
          weight={location.weight}
          onSave={(newNote, newWeight) => {
            dispatch({
              type: 'V2_SET_LOCATION_NOTE',
              payload: { serviceId, locationType, note: newNote },
            });
            dispatch({
              type: 'V2_SET_LOCATION_WEIGHT',
              payload: { serviceId, locationType, weight: newWeight },
            });
          }}
          onClose={() => setShowSettings(false)}
        />
      )}

      {!isLast && <Divider margin={['xxs', null, null, null]} />}
    </JustificationContainer>
  );
};

const CriteriaList: React.FC<{
  service: AccountService;
  dispatch: React.Dispatch<ServicesV2Action>;
}> = ({ service, dispatch }) => {
  const [newCriterionText, setNewCriterionText] = useState('');

  return (
    <JustificationContainer direction="column" gap="xs" width="100%">
      <Heading5 margin={[null]} fontWeight="regular">
        {text.criteria}
      </Heading5>

      {service.criteria.map(criterion => (
        <JustificationContainer
          key={criterion.id}
          align="center"
          gap="xs"
          width="100%"
        >
          <Body size="s" margin={[null]} style={{ flex: 1 }}>
            {criterion.text}
          </Body>
          <Icon
            name="trashcan"
            onClick={() =>
              dispatch({
                type: 'V2_REMOVE_SERVICE_CRITERION',
                payload: {
                  serviceId: service.id,
                  criterionId: criterion.id,
                },
              })
            }
          />
        </JustificationContainer>
      ))}

      <JustificationContainer align="center" gap="xs" width="100%">
        <Input
          type="text"
          value={newCriterionText}
          onChange={e => setNewCriterionText(e.target.value)}
          placeholder={text.criterionPlaceholder}
          width="100%"
          onKeyDown={e => {
            if (e.key === 'Enter' && newCriterionText.trim()) {
              dispatch({
                type: 'V2_ADD_SERVICE_CRITERION',
                payload: {
                  serviceId: service.id,
                  text: newCriterionText.trim(),
                },
              });
              setNewCriterionText('');
            }
          }}
        />
        <Button
          size="small"
          appearance="secondary"
          icon="plus"
          disabled={!newCriterionText.trim()}
          onClick={() => {
            if (newCriterionText.trim()) {
              dispatch({
                type: 'V2_ADD_SERVICE_CRITERION',
                payload: {
                  serviceId: service.id,
                  text: newCriterionText.trim(),
                },
              });
              setNewCriterionText('');
            }
          }}
        />
      </JustificationContainer>
    </JustificationContainer>
  );
};

const CalendarRow: React.FC<{
  calendar: { id: string; name: string };
  calendarRef: LfaServiceCalendarRefFieldsFragment | null;
  isLast: boolean;
  onToggle: () => void;
  onSave: (note: string, weight: number) => void;
  preferenceRank: number | null;
}> = ({ calendar, calendarRef, isLast, onToggle, onSave, preferenceRank }) => {
  const [showSettings, setShowSettings] = useState(false);
  const openerRef = useRef<HTMLDivElement>(null);
  const isSelected = !!calendarRef;
  const note = calendarRef?.note?.trim();
  const isPreferred = isSelected && (calendarRef?.weight ?? 0) > 0;

  return (
    <JustificationContainer width="100%" direction="column">
      <JustificationContainer
        width="100%"
        align="center"
        gap="s"
        justification="space-between"
      >
        <AnimatedCheckbox
          selectable
          label={
            <JustificationContainer align="center" gap="s">
              <Label
                size="s"
                fontWeight="semiBold"
                margin={['xxxs', null, null, null]}
              >
                {calendar.name || calendar.id}
                {hasValue(note) && `\u00a0(${note})`}
              </Label>
              {isPreferred && preferenceRank != null && (
                <span style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                  <LfaSettingsStatus
                    label={`${formatOrdinal(preferenceRank)} ${text.preferred} (${calendarRef.weight})`}
                    backgroundColor={{
                      group: 'success',
                      variant: 'translucent',
                    }}
                    textColor={{ group: 'success' }}
                  />
                </span>
              )}
            </JustificationContainer>
          }
          value={isSelected}
          onChange={onToggle}
        />
        <Icon
          ref={openerRef}
          name="gear"
          onClick={() => isSelected && setShowSettings(!showSettings)}
          style={{ visibility: isSelected ? 'visible' : 'hidden' }}
          aria-hidden={!isSelected}
        />
      </JustificationContainer>

      {showSettings && calendarRef && (
        <CalendarSettingsPopupV2
          openerRef={openerRef}
          note={calendarRef.note ?? ''}
          weight={calendarRef.weight}
          onSave={onSave}
          onClose={() => setShowSettings(false)}
        />
      )}

      {!isLast && <Divider margin={['xxs', null, null, null]} />}
    </JustificationContainer>
  );
};

const CalendarCheckboxes: React.FC<{
  service: AccountService;
  calendars: Array<LfaCalendarV2FieldsFragment>;
  dispatch: React.Dispatch<ServicesV2Action>;
}> = ({ service, calendars, dispatch }) => {
  const preferenceRankMap = useMemo(() => {
    const ranked = service.calendars
      .filter(c => c.weight > 0)
      .sort((a, b) => b.weight - a.weight);
    return new Map(ranked.map((c, i) => [c.calendarId, i + 1]));
  }, [service.calendars]);

  if (calendars.length === 0) {
    return (
      <Body size="s" color={{ group: 'tertiary' }} margin={[null]}>
        {text.noCalendars}
      </Body>
    );
  }

  const calendarMap = new Map(service.calendars.map(c => [c.calendarId, c]));

  const toggle = (calendarId: string) => {
    const exists = calendarMap.has(calendarId);
    const next = exists
      ? service.calendars.filter(c => c.calendarId !== calendarId)
      : [
          ...service.calendars,
          {
            __typename: 'LFA_Service_Calendar_Ref' as const,
            calendarId,
            weight: 0,
            note: null,
          },
        ];

    dispatch({
      type: 'V2_SET_SERVICE_CALENDARS',
      payload: { serviceId: service.id, calendars: next },
    });
  };

  const saveCalendarSettings = (
    calendarId: string,
    note: string,
    weight: number,
  ) => {
    dispatch({
      type: 'V2_SET_SERVICE_CALENDARS',
      payload: {
        serviceId: service.id,
        calendars: service.calendars.map(c =>
          c.calendarId === calendarId
            ? { ...c, note: note || null, weight }
            : c,
        ),
      },
    });
  };

  return (
    <JustificationContainer direction="column" gap="xs" width="100%">
      <Heading5 margin={[null]} fontWeight="regular">
        {text.calendars}
      </Heading5>
      {calendars.map((cal, idx) => (
        <CalendarRow
          key={cal.id}
          calendar={cal}
          calendarRef={calendarMap.get(cal.id) ?? null}
          isLast={idx === calendars.length - 1}
          onToggle={() => toggle(cal.id)}
          onSave={(note, weight) => saveCalendarSettings(cal.id, note, weight)}
          preferenceRank={preferenceRankMap.get(cal.id) ?? null}
        />
      ))}
    </JustificationContainer>
  );
};

const AccountServicesSection: React.FC<Props> = ({
  servicesV2,
  allServices,
  dispatch,
}) => {
  const { accountServices, officeChangesets, calendars } = servicesV2;

  const offices = useOffices({ userIds: [] });
  const officeNameMap = useMemo(
    () => new Map(offices.map(o => [o.id, o.name])),
    [offices],
  );

  const overridesByService = useMemo(() => {
    const map = new Map<
      string,
      Array<{ officeName: string; changeType: string }>
    >();

    for (const entry of officeChangesets) {
      const officeName = officeNameMap.get(entry.officeId) ?? entry.officeId;
      for (const change of entry.changeset.service) {
        const serviceId = change.serviceId;
        const existing = map.get(serviceId) ?? [];
        existing.push({ officeName, changeType: getChangeType(change) });
        map.set(serviceId, existing);
      }
    }

    return map;
  }, [officeChangesets, officeNameMap]);

  const officeAddsForService = useMemo(() => {
    const map = new Map<string, Array<string>>();

    for (const entry of officeChangesets) {
      for (const change of entry.changeset.service) {
        if (change.__typename !== 'LFA_Office_Change_Add_V2') continue;
        const officeName = officeNameMap.get(entry.officeId) ?? entry.officeId;
        const existing = map.get(change.serviceId) ?? [];
        existing.push(officeName);
        map.set(change.serviceId, existing);
      }
    }

    return map;
  }, [officeChangesets, officeNameMap]);

  const accountCalendars = useMemo(
    () => calendars.filter(cal => !cal.officeIds?.length),
    [calendars],
  );

  return (
    <GridContainer width="100%" gap="m" columns="repeat(2, 1fr)">
      {allServices.map(option => {
        const accountService = accountServices.find(s => s.id === option.id);
        const isSelected = !!accountService;
        const serviceOverrides = overridesByService.get(option.id);
        const conflictingOffices = officeAddsForService.get(option.id);
        const hasOfficeAddConflict =
          !isSelected && !!conflictingOffices && conflictingOffices.length > 0;

        return (
          <SelectBlock
            type="checkbox"
            key={option.id}
            checkboxTitle={option.name}
            description={option.description}
            checked={isSelected}
            margin={[null]}
            checkboxOnly
            onClick={() => {
              if (hasOfficeAddConflict) return;
              dispatch({
                type: 'V2_TOGGLE_ACCOUNT_SERVICE',
                payload: { serviceId: option.id },
              });
            }}
          >
            {hasOfficeAddConflict && (
              <JustificationContainer
                width="100%"
                padding={['s', 'xl', null, 'xl']}
                direction="column"
                gap="xs"
              >
                <LfaSettingsStatus
                  label={text.officeAddConflict(conflictingOffices)}
                  backgroundColor={{
                    group: 'danger',
                    variant: 'translucent',
                  }}
                  textColor={{ group: 'danger' }}
                />
              </JustificationContainer>
            )}
            {isSelected && accountService && (
              <JustificationContainer
                width="100%"
                padding={['s', 'xl', null, 'xl']}
                direction="column"
                gap="s"
                onClick={e => e.stopPropagation()}
                style={{ cursor: 'initial' }}
              >
                <Divider margin={[null]} />

                {serviceOverrides && serviceOverrides.length > 0 && (
                  <>
                    <LfaSettingsStatus
                      label={text.officesOverride(serviceOverrides.length)}
                      backgroundColor={{
                        group: 'warning',
                        variant: 'translucent',
                      }}
                      textColor={{ group: 'warning' }}
                    />
                    <JustificationContainer
                      direction="column"
                      gap="xxs"
                      width="100%"
                    >
                      {serviceOverrides.map(({ officeName, changeType }) => (
                        <Body
                          key={officeName}
                          size="s"
                          color={{ group: 'tertiary' }}
                          margin={[null]}
                        >
                          {officeName}: {changeType}
                        </Body>
                      ))}
                    </JustificationContainer>
                    <Divider margin={[null]} />
                  </>
                )}

                <Heading5 margin={[null]} fontWeight="regular" required>
                  {text.selectLocations}
                </Heading5>
                {accountService.locations.length === 0 && (
                  <JustificationContainer align="center" gap="xs" width="100%">
                    <LfaSettingsStatus
                      label={text.locationError}
                      backgroundColor={{
                        group: 'danger',
                        variant: 'translucent',
                      }}
                      textColor={{ group: 'danger' }}
                    />
                  </JustificationContainer>
                )}
                <JustificationContainer
                  direction="column"
                  gap="xs"
                  width="100%"
                >
                  {ALL_LOCATION_TYPES.map((locationType, index) => (
                    <LocationItemV2
                      key={locationType}
                      serviceId={option.id}
                      locationType={locationType}
                      location={accountService.locations.find(
                        l => l.type === locationType,
                      )}
                      isLast={index === ALL_LOCATION_TYPES.length - 1}
                      dispatch={dispatch}
                    />
                  ))}
                </JustificationContainer>

                <Divider margin={['xxs', null]} />

                <CriteriaList service={accountService} dispatch={dispatch} />

                <Divider margin={['xxs', null]} />

                <CalendarCheckboxes
                  service={accountService}
                  calendars={accountCalendars}
                  dispatch={dispatch}
                />
              </JustificationContainer>
            )}
          </SelectBlock>
        );
      })}
    </GridContainer>
  );
};

export default AccountServicesSection;
